/**
 * Service API moderne pour les abonnements et tokens
 * Basé sur la documentation API backend mise à jour
 */

import { 
  SubscriptionPlan,
  CustomerTokenBalance,
  TokenTransaction,
  Subscription,
  CreateSubscriptionData,
  GetPlansParams,
  GetTokenTransactionsParams,
  CustomerType,
  BillingPeriod,
  SubscriptionStatus,
  TokenTransactionType,
  convertToLegacyPlan,
  LegacyPricingPlan,
  PlansConfig,
  PlansApiResponse,
  EffectiveStatusResponse,
  GracePeriodInfo
} from '../types/subscription';
import { ensureFreshToken } from '../utils/tokenRefresh';

// ===== DONNÉES PAR DÉFAUT (FALLBACK) =====
// Ces données sont utilisées si le backend est indisponible

const DEFAULT_CONFIG: PlansConfig = {
  baseCurrency: 'USD',
  defaultAnnualDiscountPercentage: 16.67,
  defaultTokenPricePerMillion: 10,
  volumeDiscounts: [
    { minTokens: 1000000, discountPercentage: 5 },
    { minTokens: 5000000, discountPercentage: 10 },
    { minTokens: 10000000, discountPercentage: 15 },
    { minTokens: 50000000, discountPercentage: 20 }
  ],
  defaultTokenRolloverMonths: 3,
  maxTokenRolloverMonths: 6,
  defaultFeatureLimits: {
    freeUsers: 1,
    standardUsers: 5,
    premiumUsers: 20,
    enterpriseUsers: 100
  },
  trialPeriods: {
    sme: { durationDays: 14, includedTokens: 500000 },
    financial: { durationDays: 30, includedTokens: 2000000 }
  }
};

// Plans SME par défaut (basés sur la structure backend - Juin 2025)
const DEFAULT_SME_PLANS: SubscriptionPlan[] = [
  {
    id: 'sme-freemium',
    configId: 'sme-freemium',
    name: 'Freemium',
    description: 'Découvrez Wanzo gratuitement avec les fonctionnalités essentielles',
    customerType: CustomerType.SME,
    billingPeriod: BillingPeriod.MONTHLY,
    monthlyPriceUSD: 0,
    annualPriceUSD: 0,
    annualDiscountPercentage: 0,
    tokenAllocation: {
      monthlyTokens: 30,
      tokenRollover: false,
      maxRolloverMonths: 0
    },
    features: {
      // Commercial & Produits
      accounting_entries_monthly: { enabled: true, limit: 900 },
      invoices_monthly: { enabled: true, limit: 250 },
      products_limit: { enabled: true, limit: 200 },
      branches_limit: { enabled: true, limit: 1 },
      pos_limit: { enabled: true, limit: 1 },
      // Utilisateurs
      users_limit: { enabled: true, limit: 2 },
      // IA
      ai_messages_monthly: { enabled: true, limit: 300 },
      adha_contexts_limit: { enabled: true, limit: 1 },
      // Credit Score & Financement
      credit_score_access: { enabled: true },
      financing_access: { enabled: false },
      financing_max_amount_usd: { enabled: false, limit: 0 },
      // Support & Stockage
      human_expertise_access: { enabled: false },
      storage_gb: { enabled: true, limit: 1 },
      data_exports_monthly: { enabled: true, limit: 1 },
      api_calls_daily: { enabled: true, limit: 100 }
    },
    isPopular: false,
    isVisible: true,
    sortOrder: 1,
    tags: ['gratuit', 'débutant', 'essentiel'],
    metadata: { isDefaultPlan: true, version: 2 }
  },
  {
    id: 'sme-basic',
    configId: 'sme-basic',
    name: 'Basic',
    description: 'Plan idéal pour les petites entreprises en croissance',
    customerType: CustomerType.SME,
    billingPeriod: BillingPeriod.MONTHLY,
    monthlyPriceUSD: 9.9,
    annualPriceUSD: 99,
    annualDiscountPercentage: 16.67,
    tokenAllocation: {
      monthlyTokens: 500,
      tokenRollover: true,
      maxRolloverMonths: 2
    },
    features: {
      // Commercial & Produits
      accounting_entries_monthly: { enabled: true, limit: 1800 },
      invoices_monthly: { enabled: true, limit: 600 },
      products_limit: { enabled: true, limit: 500 },
      branches_limit: { enabled: true, limit: 2 },
      pos_limit: { enabled: true, limit: 3 },
      // Utilisateurs
      users_limit: { enabled: true, limit: 3 },
      // IA
      ai_messages_monthly: { enabled: true, limit: 1000 },
      adha_contexts_limit: { enabled: true, limit: 3 },
      // Credit Score & Financement
      credit_score_access: { enabled: true },
      financing_access: { enabled: true },
      financing_max_amount_usd: { enabled: true, limit: 500 },
      // Support & Stockage
      human_expertise_access: { enabled: false },
      storage_gb: { enabled: true, limit: 5 },
      data_exports_monthly: { enabled: true, limit: 10 },
      api_calls_daily: { enabled: true, limit: 1000 }
    },
    isPopular: false,
    isVisible: true,
    sortOrder: 2,
    tags: ['abordable', 'pme', 'croissance'],
    metadata: { isDefaultPlan: true, version: 2 }
  },
  {
    id: 'sme-premium',
    configId: 'sme-premium',
    name: 'Premium',
    description: 'Plan complet pour PME avec toutes les fonctionnalités et financement',
    customerType: CustomerType.SME,
    billingPeriod: BillingPeriod.MONTHLY,
    monthlyPriceUSD: 19.9,
    annualPriceUSD: 199,
    annualDiscountPercentage: 16.67,
    tokenAllocation: {
      monthlyTokens: -1, // Illimité
      tokenRollover: true,
      maxRolloverMonths: 3
    },
    features: {
      // Commercial & Produits
      accounting_entries_monthly: { enabled: true, limit: -1 },
      invoices_monthly: { enabled: true, limit: -1 },
      products_limit: { enabled: true, limit: -1 },
      branches_limit: { enabled: true, limit: 5 },
      pos_limit: { enabled: true, limit: 10 },
      // Utilisateurs
      users_limit: { enabled: true, limit: 10 },
      // IA
      ai_messages_monthly: { enabled: true, limit: -1 },
      adha_contexts_limit: { enabled: true, limit: 10 },
      // Credit Score & Financement
      credit_score_access: { enabled: true },
      financing_access: { enabled: true },
      financing_max_amount_usd: { enabled: true, limit: 2500 },
      // Support & Stockage
      human_expertise_access: { enabled: true },
      storage_gb: { enabled: true, limit: 20 },
      data_exports_monthly: { enabled: true, limit: -1 },
      api_calls_daily: { enabled: true, limit: 10000 }
    },
    isPopular: true,
    isVisible: true,
    sortOrder: 3,
    tags: ['populaire', 'complet', 'financement', 'premium'],
    metadata: { isDefaultPlan: true, version: 2 }
  }
];

// Plans Institution Financière par défaut (Juin 2025)
const DEFAULT_FI_PLANS: SubscriptionPlan[] = [
  {
    id: 'institution-freemium',
    configId: 'institution-freemium',
    name: 'Découverte',
    description: 'Découvrez notre plateforme institution gratuitement',
    customerType: CustomerType.FINANCIAL_INSTITUTION,
    billingPeriod: BillingPeriod.MONTHLY,
    monthlyPriceUSD: 0,
    annualPriceUSD: 0,
    annualDiscountPercentage: 0,
    tokenAllocation: {
      monthlyTokens: 30,
      tokenRollover: false,
      maxRolloverMonths: 0
    },
    features: {
      // Portefeuille
      portfolio_companies_limit: { enabled: true, limit: 50 },
      portfolios_limit: { enabled: true, limit: 2 },
      portfolio_volume_usd: { enabled: true, limit: 100000 },
      // Agences
      agencies_limit: { enabled: true, limit: 1 },
      coverage_zones_limit: { enabled: true, limit: 2 },
      // Utilisateurs
      users_limit: { enabled: true, limit: 1 },
      // IA
      ai_messages_monthly: { enabled: true, limit: 30 },
      adha_contexts_limit: { enabled: true, limit: 1 },
      adha_advanced_analytics: { enabled: false },
      // Évaluations
      risk_assessments_monthly: { enabled: true, limit: 10 },
      credit_applications_monthly: { enabled: false, limit: 0 },
      // Support & Stockage
      human_expertise_access: { enabled: false },
      storage_gb: { enabled: true, limit: 1 },
      data_exports_monthly: { enabled: true, limit: 1 },
      api_calls_daily: { enabled: true, limit: 100 }
    },
    isPopular: false,
    isVisible: true,
    sortOrder: 4,
    tags: ['gratuit', 'découverte', 'institution'],
    metadata: { isDefaultPlan: true, version: 2 }
  },
  {
    id: 'institution-basic',
    configId: 'institution-basic',
    name: 'Basic Institution',
    description: 'Pour les institutions financières en démarrage',
    customerType: CustomerType.FINANCIAL_INSTITUTION,
    billingPeriod: BillingPeriod.MONTHLY,
    monthlyPriceUSD: 99,
    annualPriceUSD: 999,
    annualDiscountPercentage: 16.67,
    tokenAllocation: {
      monthlyTokens: 500,
      tokenRollover: true,
      maxRolloverMonths: 2
    },
    features: {
      // Portefeuille
      portfolio_companies_limit: { enabled: true, limit: 500 },
      portfolios_limit: { enabled: true, limit: 10 },
      portfolio_volume_usd: { enabled: true, limit: 1000000 },
      // Agences
      agencies_limit: { enabled: true, limit: 5 },
      coverage_zones_limit: { enabled: true, limit: 10 },
      // Utilisateurs
      users_limit: { enabled: true, limit: 3 },
      // IA
      ai_messages_monthly: { enabled: true, limit: 500 },
      adha_contexts_limit: { enabled: true, limit: 5 },
      adha_advanced_analytics: { enabled: false },
      // Évaluations
      risk_assessments_monthly: { enabled: true, limit: 100 },
      credit_applications_monthly: { enabled: true, limit: 50 },
      // Support & Stockage
      human_expertise_access: { enabled: false },
      storage_gb: { enabled: true, limit: 10 },
      data_exports_monthly: { enabled: true, limit: 20 },
      api_calls_daily: { enabled: true, limit: 5000 }
    },
    isPopular: false,
    isVisible: true,
    sortOrder: 5,
    tags: ['institution', 'démarrage', 'basic'],
    metadata: { isDefaultPlan: true, version: 2 }
  },
  {
    id: 'institution-premium',
    configId: 'institution-premium',
    name: 'Premium Institution',
    description: 'Solution complète pour institutions financières et fonds d\'investissement',
    customerType: CustomerType.FINANCIAL_INSTITUTION,
    billingPeriod: BillingPeriod.MONTHLY,
    monthlyPriceUSD: 299,
    annualPriceUSD: 2999,
    annualDiscountPercentage: 16.67,
    tokenAllocation: {
      monthlyTokens: -1, // Illimité
      tokenRollover: true,
      maxRolloverMonths: 6
    },
    features: {
      // Portefeuille
      portfolio_companies_limit: { enabled: true, limit: -1 },
      portfolios_limit: { enabled: true, limit: -1 },
      portfolio_volume_usd: { enabled: true, limit: -1 },
      // Agences
      agencies_limit: { enabled: true, limit: -1 },
      coverage_zones_limit: { enabled: true, limit: -1 },
      // Utilisateurs
      users_limit: { enabled: true, limit: 20 },
      // IA
      ai_messages_monthly: { enabled: true, limit: -1 },
      adha_contexts_limit: { enabled: true, limit: -1 },
      adha_advanced_analytics: { enabled: true },
      // Évaluations
      risk_assessments_monthly: { enabled: true, limit: -1 },
      credit_applications_monthly: { enabled: true, limit: -1 },
      // Support & Stockage
      human_expertise_access: { enabled: true },
      storage_gb: { enabled: true, limit: 50 },
      data_exports_monthly: { enabled: true, limit: -1 },
      api_calls_daily: { enabled: true, limit: 50000 }
    },
    isPopular: true,
    isVisible: true,
    sortOrder: 6,
    tags: ['institution', 'professionnel', 'prospection', 'portfolio', 'premium'],
    metadata: { isDefaultPlan: true, version: 2 }
  }
];

const MOCK_TOKEN_BALANCE: CustomerTokenBalance = {
  id: 'balance-1',
  customerId: 'user_auth0_mock_id',
  totalTokens: 30, // Freemium: 30 messages IA/mois
  usedTokens: 0,
  remainingTokens: 30,
  monthlyAllocation: 30,
  rolledOverTokens: 0,
  bonusTokens: 0,
  currentPeriod: '2026-01',
  periodStartDate: new Date('2026-01-01'),
  periodEndDate: new Date('2026-01-31'),
  rolloverHistory: []
};

// ✨ Plan Freemium par défaut pour les nouveaux utilisateurs SME
const MOCK_SUBSCRIPTION: Subscription = {
  id: 'sub-freemium-default',
  customerId: 'user_auth0_mock_id',
  planId: 'sme-freemium',
  plan: DEFAULT_SME_PLANS[0], // ✨ Freemium = index 0
  status: SubscriptionStatus.ACTIVE,
  startDate: new Date().toISOString(),
  currentPeriodStart: new Date().toISOString(),
  currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  cancelAtPeriodEnd: false,
  paymentMethod: {
    type: 'mobile',
    mobileMoneyProvider: '',
    mobileMoneyNumber: ''
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

class ModernSubscriptionService {
  // Cache de configuration
  private cachedConfig: PlansConfig | null = null;
  private cachedPlans: SubscriptionPlan[] = [];
  private lastFetch: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Récupère la configuration globale des plans
   */
  getConfig(): PlansConfig {
    return this.cachedConfig || DEFAULT_CONFIG;
  }

  /**
   * Récupère tous les plans d'abonnement depuis le backend avec fallback vers les defaults
   */
  async getSubscriptionPlans(params?: GetPlansParams): Promise<SubscriptionPlan[]> {
    try {
      // Vérifier le cache
      const now = Date.now();
      if (this.cachedPlans.length > 0 && (now - this.lastFetch) < this.CACHE_DURATION) {
        let plans = [...this.cachedPlans];
        if (params?.customerType) {
          plans = plans.filter(plan => plan.customerType === params.customerType);
        }
        return plans;
      }

      // Étape 1: Construire l'URL avec les paramètres
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/land/api/v1';
      const queryParams = new URLSearchParams();
      
      if (params?.customerType) {
        queryParams.set('customerType', params.customerType);
      }
      if (params?.billingPeriod) {
        queryParams.set('billingPeriod', params.billingPeriod);
      }
      
      const url = `${baseUrl}/pricing/plans${queryParams.toString() ? `?${queryParams}` : ''}`;
      
      // Étape 2: Récupérer le token d'authentification (avec refresh si expiré)
      const token = await ensureFreshToken();
      
      // Étape 3: Appeler l'API backend
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        // Timeout de 5 secondes
        signal: AbortSignal.timeout(5000)
      });
      
      if (!response.ok) {
        throw new Error(`Backend responded with ${response.status}: ${response.statusText}`);
      }
      
      const result: PlansApiResponse = await response.json();
      
      // Support des deux formats de réponse: { plans: [], config: {} } ou { data: [] }
      let plans: SubscriptionPlan[];
      if (result.plans && Array.isArray(result.plans)) {
        plans = result.plans;
        // Stocker la configuration si disponible
        if (result.config) {
          this.cachedConfig = result.config;
        }
      } else if (Array.isArray((result as any).data)) {
        plans = (result as any).data;
      } else if (Array.isArray(result)) {
        plans = result as unknown as SubscriptionPlan[];
      } else {
        throw new Error('Invalid response format from backend');
      }
      
      // Filtrer les plans non visibles
      plans = plans.filter(plan => plan.isVisible !== false);
      
      // Trier par sortOrder
      plans.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      
      // Mettre en cache
      this.cachedPlans = plans;
      this.lastFetch = now;
      
      console.log('✅ Plans chargés depuis le backend:', plans.length, 'plan(s)', result.source || '');
      
      // Appliquer le filtrage si demandé
      if (params?.customerType) {
        plans = plans.filter(plan => plan.customerType === params.customerType);
      }
      
      return plans;
      
    } catch (error) {
      // Étape 4: FALLBACK vers les defaults en cas d'erreur
      console.warn('⚠️ Backend indisponible, utilisation des plans par défaut:', error instanceof Error ? error.message : error);
      
      // Utiliser les plans par défaut
      let plans = [...DEFAULT_SME_PLANS, ...DEFAULT_FI_PLANS];
      
      // Appliquer le filtrage local pour le fallback
      if (params?.customerType) {
        plans = plans.filter(plan => plan.customerType === params.customerType);
      }
      
      if (params?.billingPeriod) {
        plans = plans.filter(plan => plan.billingPeriod === params.billingPeriod);
      }
      
      console.log('📦 Plans fallback chargés:', plans.length, 'plan(s)');
      return plans;
    }
  }

  /**
   * Récupère un plan spécifique par ID
   */
  async getSubscriptionPlan(planId: string): Promise<SubscriptionPlan | null> {
    // D'abord essayer depuis le cache/API
    const allPlans = await this.getSubscriptionPlans();
    const found = allPlans.find(plan => plan.id === planId || plan.configId === planId);
    if (found) return found;
    
    // Fallback vers les plans par défaut
    const defaultPlans = [...DEFAULT_SME_PLANS, ...DEFAULT_FI_PLANS];
    return defaultPlans.find(plan => plan.id === planId || plan.configId === planId) || null;
  }

  /**
   * Récupère l'abonnement actuel de l'utilisateur
   */
  async getCurrentSubscription(): Promise<Subscription | null> {
    try {
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/land/api/v1';
      const token = await ensureFreshToken();
      
      const response = await fetch(`${baseUrl}/subscription/current`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: AbortSignal.timeout(5000)
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          // Pas d'abonnement actif
          return null;
        }
        if (response.status === 401) {
          throw new Error('Session expirée. Veuillez vous reconnecter.');
        }
        throw new Error(`Erreur serveur: ${response.status}`);
      }
      
      const result = await response.json();
      return result.data || result;
    } catch (error) {
      console.error('❌ Erreur getCurrentSubscription:', error);
      throw error;
    }
  }

  /**
   * 🆕 Récupère le statut effectif de l'abonnement
   * GET /subscription/effective-status
   * 
   * Endpoint RECOMMANDÉ pour vérifier l'accès utilisateur.
   * Inclut la gestion du grace period, downgrades et messages de statut.
   */
  async getEffectiveStatus(): Promise<EffectiveStatusResponse | null> {
    try {
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/land/api/v1';
      const token = await ensureFreshToken();
      
      const response = await fetch(`${baseUrl}/subscription/effective-status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: AbortSignal.timeout(5000)
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          // Pas d'abonnement actif - retourner un statut freemium
          return null;
        }
        if (response.status === 401) {
          throw new Error('Session expirée. Veuillez vous reconnecter.');
        }
        throw new Error(`Erreur serveur: ${response.status}`);
      }
      
      const result = await response.json();
      return result.data || result;
    } catch (error) {
      console.error('❌ Erreur getEffectiveStatus:', error);
      // Fallback vers getCurrentSubscription
      try {
        const subscription = await this.getCurrentSubscription();
        if (!subscription) return null;
        
        return {
          subscription,
          customer: {
            id: subscription.customerId,
            email: '',
            type: 'sme' as CustomerType
          },
          isDowngraded: false,
          gracePeriod: {
            inGracePeriod: false,
            daysRemaining: 0
          },
          planName: subscription.plan?.name || 'Freemium',
          planTier: (subscription.plan?.name?.toLowerCase() || 'freemium') as 'freemium' | 'standard' | 'premium' | 'enterprise',
          statusMessage: subscription.status === 'active' ? 'Abonnement actif' : 'Statut inconnu'
        };
      } catch {
        throw error;
      }
    }
  }

  /**
   * Crée un nouvel abonnement avec paiement
   */
  async createSubscription(data: CreateSubscriptionData): Promise<Subscription> {
    try {
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/land/api/v1';
      const token = await ensureFreshToken();
      
      console.log('💳 Création d\'abonnement via API...', data);
      
      // Résolution de la quantité pour les plans `per_unit` (coopératives).
      // Source de vérité par ordre de priorité :
      //  1. data.quantity (caller a explicitement fourni la valeur)
      //  2. sessionStorage.cooperative_member_count (slider Pricing.tsx)
      //  3. omis → backend default 1 (transparent pour les plans flat)
      let resolvedQuantity = data.quantity;
      if (resolvedQuantity == null && typeof window !== 'undefined') {
        try {
          const stored = sessionStorage.getItem('cooperative_member_count');
          const parsed = stored != null ? parseInt(stored, 10) : NaN;
          if (Number.isFinite(parsed) && parsed >= 1) {
            resolvedQuantity = parsed;
          }
        } catch {
          // sessionStorage indisponible — ignore, backend défaut 1
        }
      }

      const response = await fetch(`${baseUrl}/subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          planId: data.planId,
          billingPeriod: data.billingPeriod || 'monthly',
          paymentMethod: {
            type: data.paymentMethod.type,
            // Carte bancaire
            cardNumber: data.paymentMethod.cardNumber,
            expiryMonth: data.paymentMethod.expiryMonth,
            expiryYear: data.paymentMethod.expiryYear,
            cvc: data.paymentMethod.cvc,
            name: data.paymentMethod.name,
            // Mobile Money
            provider: data.paymentMethod.mobileMoneyProvider,
            phoneNumber: data.paymentMethod.mobileMoneyNumber,
            pin: data.paymentMethod.mobilePin
          },
          billingDetails: data.billingDetails,
          // Inclus uniquement si défini — le backend l'ignore pour les plans flat
          ...(resolvedQuantity != null ? { quantity: resolvedQuantity } : {}),
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erreur de paiement: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('✅ Abonnement créé avec succès:', result);
      return result.data || result;
    } catch (error) {
      console.error('❌ Erreur création abonnement:', error);
      
      // En cas d'erreur réseau, on ne fallback PAS sur le mock pour les paiements
      // Il faut que le paiement soit réellement traité
      throw error;
    }
  }

  /**
   * Récupère le solde de tokens de l'utilisateur
   */
  async getTokenBalance(): Promise<CustomerTokenBalance> {
    try {
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/land/api/v1';
      const token = await ensureFreshToken();
      
      const response = await fetch(`${baseUrl}/tokens/balance`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: AbortSignal.timeout(5000)
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expirée. Veuillez vous reconnecter.');
        }
        throw new Error(`Erreur serveur: ${response.status}`);
      }
      
      const result = await response.json();
      return result.data || result;
    } catch (error) {
      console.error('❌ Erreur getTokenBalance:', error);
      throw error;
    }
  }

  /**
   * Récupère l'historique des transactions de tokens
   */
  async getTokenTransactions(_params?: GetTokenTransactionsParams): Promise<{
    transactions: TokenTransaction[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Mock transactions
    const mockTransactions: TokenTransaction[] = [
      {
        id: 'tx-1',
        customerId: 'user_auth0_mock_id',
        transactionType: TokenTransactionType.ALLOCATION,
        tokenAmount: 2000000,
        balanceBefore: 0,
        balanceAfter: 2000000,
        description: 'Allocation mensuelle - PME Standard',
        createdAt: new Date('2025-08-01')
      },
      {
        id: 'tx-2',
        customerId: 'user_auth0_mock_id',
        transactionType: TokenTransactionType.ALLOCATION,
        tokenAmount: 2000000,
        balanceBefore: 2000000,
        balanceAfter: 4000000,
        description: 'Allocation supplémentaire plan',
        createdAt: new Date('2025-08-05')
      },
      {
        id: 'tx-3',
        customerId: 'user_auth0_mock_id',
        transactionType: TokenTransactionType.USAGE,
        tokenAmount: -50000,
        balanceBefore: 7000000,
        balanceAfter: 6950000,
        description: 'Analyse de document IA',
        featureCode: 'document_analysis',
        createdAt: new Date('2025-08-10')
      }
    ];

    return {
      transactions: mockTransactions,
      pagination: {
        page: 1,
        limit: 20,
        total: mockTransactions.length,
        pages: 1
      }
    };
  }

  // === MÉTHODES DE COMPATIBILITÉ ===
  
  /**
   * Récupère les plans au format legacy pour compatibilité
   * @deprecated Utiliser getSubscriptionPlans à la place
   */
  async getAvailablePlans(): Promise<LegacyPricingPlan[]> {
    const plans = await this.getSubscriptionPlans();
    return plans.map(convertToLegacyPlan);
  }

  /**
   * Données d'abonnement au format legacy
   * @deprecated Utiliser getCurrentSubscription et getTokenBalance à la place
   */
  async getSubscriptionData(userId: string): Promise<{
    id: string;
    userId: string;
    currentPlan: string;
    tokenBalance: number;
    tokenTotal: number;
    paymentHistory: any[];
  }> {
    const [subscription, tokenBalance] = await Promise.all([
      this.getCurrentSubscription(),
      this.getTokenBalance()
    ]);

    return {
      id: subscription?.id || 'mock-sub',
      userId,
      currentPlan: subscription?.plan.name || 'PME',
      tokenBalance: tokenBalance.remainingTokens,
      tokenTotal: tokenBalance.totalTokens,
      paymentHistory: [] // À implémenter si nécessaire
    };
  }

  /**
   * ✨ Obtient le plan par défaut selon le type de client
   * SME -> sme-freemium, Institution -> institution
   */
  async getDefaultPlanByCustomerType(customerType: CustomerType): Promise<SubscriptionPlan | null> {
    const plans = await this.getSubscriptionPlans({ customerType });
    
    // Chercher le plan marqué comme défaut
    const defaultPlan = plans.find(plan => plan.metadata?.isDefaultPlan === true);
    if (defaultPlan) return defaultPlan;
    
    // Fallback: Le plan gratuit pour SME, le premier plan pour Institution
    if (customerType === CustomerType.SME) {
      return plans.find(plan => plan.monthlyPriceUSD === 0) || plans[0] || null;
    }
    
    // Pour les institutions, retourner le premier plan disponible
    return plans[0] || null;
  }

  /**
   * ✨ Crée un abonnement Freemium pour un nouveau compte SME
   * Appelé automatiquement lors de la création d'un nouveau compte
   */
  async initializeFreemiumSubscription(customerId: string): Promise<Subscription> {
    const freemiumPlan = await this.getDefaultPlanByCustomerType(CustomerType.SME);
    
    if (!freemiumPlan) {
      throw new Error('Plan Freemium non disponible');
    }

    return this.createSubscription({
      planId: freemiumPlan.id,
      billingPeriod: BillingPeriod.MONTHLY,
      paymentMethod: {
        type: 'mobile', // Pas de paiement requis pour Freemium
        mobileMoneyProvider: '',
        mobileMoneyNumber: ''
      }
    });
  }

  /**
   * ✨ Vérifie si un utilisateur a un abonnement actif
   * Retourne false si pas d'abonnement ou abonnement expiré
   */
  async hasActiveSubscription(): Promise<boolean> {
    const subscription = await this.getCurrentSubscription();
    if (!subscription) return false;
    
    return subscription.status === SubscriptionStatus.ACTIVE;
  }
}

// Export singleton
export default new ModernSubscriptionService();

// Export des plans par défaut pour les tests et fallbacks
export { DEFAULT_SME_PLANS, DEFAULT_FI_PLANS };
